import { useId } from "react";
import { BOARD } from "@grundbesitz/shared";
import type { PlayerState, PublicGameState, TradeSide } from "@grundbesitz/shared";
import { assetGroupOf, jailCardLabel, tradableFields } from "../../../lib/assets";
import { formatMoney } from "../../../lib/format";
import { Button } from "../../ui/Button";

const QUICK_CASH = [10, 50, 100] as const;

const toggle = <T,>(list: T[], item: T): T[] => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

/** One side of the offer editor: cash, the owner's properties as toggle cards, and jail cards. */
export function SidePicker({
  game,
  owner,
  title,
  side,
  onChange,
}: {
  game: PublicGameState;
  owner: PlayerState;
  title: string;
  side: TradeSide;
  onChange: (side: TradeSide) => void;
}) {
  const uid = useId();
  const fields = tradableFields(game, owner.id);
  const clamp = (n: number) => Math.max(0, Math.min(owner.cash, Math.floor(Number.isFinite(n) ? n : 0)));

  return (
    <fieldset className="@container min-w-0 rounded-xl border border-line bg-surface p-3">
      <legend className="px-1 font-display text-base font-semibold">{title}</legend>

      <div>
        <label htmlFor={`${uid}-cash`} className="text-sm font-semibold">
          Bargeld
        </label>
        <div className="mt-1 flex items-center gap-2">
          <div className="relative flex-1">
            <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-2">
              M
            </span>
            <input
              id={`${uid}-cash`}
              type="number"
              inputMode="numeric"
              min={0}
              max={owner.cash}
              step={1}
              value={side.cash === 0 ? "" : side.cash}
              placeholder="0"
              aria-describedby={`${uid}-cash-hint`}
              onChange={(e) => onChange({ ...side, cash: clamp(e.target.valueAsNumber) })}
              className="min-h-11 w-full rounded-lg border border-line bg-bg pl-8 pr-2 text-right tabular-nums"
            />
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {QUICK_CASH.map((n) => (
            <Button
              key={n}
              variant="secondary"
              className="min-w-14 flex-1 px-2"
              disabled={side.cash >= owner.cash}
              aria-label={`${n} M dazu`}
              onClick={() => onChange({ ...side, cash: clamp(side.cash + n) })}
            >
              +{n}
            </Button>
          ))}
          <Button
            variant="ghost"
            className="px-3"
            disabled={side.cash === 0}
            onClick={() => onChange({ ...side, cash: 0 })}
          >
            Leeren
          </Button>
        </div>
        <p id={`${uid}-cash-hint`} className="mt-1 text-xs text-ink-2">
          {owner.name} hat {formatMoney(owner.cash)}. Mehr geht nicht.
        </p>
      </div>

      <div className="mt-3">
        <p className="text-sm font-semibold">Grundstücke</p>
        {fields.length === 0 ? (
          <p className="mt-1 text-sm text-ink-2">{owner.name} besitzt keine Grundstücke.</p>
        ) : (
          <ul className="mt-1.5 grid grid-cols-1 gap-1.5 @[17rem]:grid-cols-2">
            {fields.map(({ field, mortgaged, blocked }) => {
              const group = assetGroupOf(field);
              const selected = side.fields.includes(field);
              return (
                <li key={field}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    disabled={blocked !== null}
                    onClick={() => onChange({ ...side, fields: toggle(side.fields, field) })}
                    className={`relative flex min-h-11 w-full flex-col items-start justify-center overflow-hidden rounded-lg border-2 py-1.5 pl-4 pr-2 text-left transition-colors disabled:opacity-60 ${
                      selected ? "border-accent bg-accent-soft" : "border-line bg-bg hover:bg-surface-2"
                    }`}
                  >
                    <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5" style={{ background: group?.color }} />
                    <span className="flex w-full items-center gap-1.5">
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{BOARD[field].name}</span>
                      {selected && <span aria-hidden="true" className="text-accent">✓</span>}
                    </span>
                    <span className="text-xs text-ink-2">
                      {group?.name}
                      {mortgaged ? ", belastet" : ""}
                    </span>
                    {blocked && <span className="text-xs font-semibold text-warn">{blocked}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {owner.jailCards.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-semibold">Freikarten</p>
          <ul className="mt-1.5 space-y-1.5">
            {owner.jailCards.map((c) => {
              const selected = side.jailCards.includes(c);
              return (
                <li key={c}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onChange({ ...side, jailCards: toggle(side.jailCards, c) })}
                    className={`flex min-h-11 w-full items-center justify-between rounded-lg border-2 px-3 text-left text-sm font-semibold transition-colors ${
                      selected ? "border-accent bg-accent-soft" : "border-line bg-bg hover:bg-surface-2"
                    }`}
                  >
                    {jailCardLabel(c)}
                    {selected && <span aria-hidden="true" className="text-accent">✓</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </fieldset>
  );
}
